import { lazy, Suspense, useEffect, type ReactElement } from "react";
import { Spinner } from "./components/ui";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PreferencesDeepLink } from "./components/modals/PreferencesDeepLink";
import { LandingScreen } from "./screens/Landing";
import { buildRaceDayRedirect } from "./lib/raceDayRedirect";

/**
 * Lazy route screens keep non-landing paths off the critical bundle. `LandingScreen`
 * is eager so the default `/` → `/landing` path never requires a second chunk fetch
 * under the Shopify app proxy (avoids “Failed to fetch dynamically imported module”
 * when `index.js` and hashed chunks are briefly out of sync after deploy or CDN cache).
 */
const DashboardScreen = lazy(() =>
  import("./screens/Dashboard").then((m) => ({ default: m.DashboardScreen }))
);
// "My Training" replaces the standalone Training Planner + My Activities pages
// (docs/my-training). `/planner` and `/activities` now redirect here.
const MyTrainingScreen = lazy(() =>
  import("./screens/MyTraining").then((m) => ({ default: m.MyTrainingScreen }))
);
// "My Fuel" — the fuel journey's home (journeys IA split, lane A). Hosts the box
// timeline standalone; My Training stays calendar-first.
const MyFuelScreen = lazy(() =>
  import("./screens/MyFuel").then((m) => ({ default: m.MyFuelScreen }))
);
const FuelPlanScreen = lazy(() =>
  import("./screens/FuelPlan").then((m) => ({ default: m.FuelPlanScreen }))
);
const PlanPendingScreen = lazy(() =>
  import("./screens/PlanPending").then((m) => ({ default: m.PlanPendingScreen }))
);
const PlansScreen = lazy(() => import("./screens/Plans").then((m) => ({ default: m.PlansScreen })));
const ShareScreen = lazy(() => import("./screens/Share").then((m) => ({ default: m.ShareScreen })));
const FaqScreen = lazy(() => import("./screens/Faq").then((m) => ({ default: m.FaqScreen })));
const NotFoundScreen = lazy(() =>
  import("./screens/NotFound").then((m) => ({ default: m.NotFoundScreen }))
);

/** Shown briefly while a lazy route chunk loads (matches Gate visuals for continuity). */
function RouteShellFallback(): ReactElement {
  return (
    <div className="fg fg-gate">
      <Spinner accessibilityLabel="Loading" className="fg-gate__spinner" />
    </div>
  );
}

/**
 * Retired race-day screen — race-day lives on the My Fuel timeline (`#race-day`).
 * Preserve the incoming query string (F-01-10) so utm_* attribution survives the
 * bounce, AND thread the `:journeyId` path param through as `?journey=` so MyFuel
 * selects the LINKED race before scrolling — without it, a link to a later race
 * lands on the first/default journey when several are active (F-X1-3).
 */
function RedirectToMyFuelRaceDay() {
  const location = useLocation();
  const { journeyId } = useParams();
  return <Navigate to={buildRaceDayRedirect(journeyId, location.search)} replace />;
}

/** Keep OAuth return query flags when bouncing a retired path. */
function NavigatePreserveSearch({ to }: { to: string }) {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`${to}${qs ? `?${qs}` : ""}`} replace />;
}

/**
 * Client-side route table. The server's app-proxy serves the same SPA shell for
 * every client route (see the `appFactory` proxy GET list), so the SPA owns
 * which screen each URL renders. Paths with extra segments (`/plan/pending/:id`)
 * must be listed explicitly there — `/plan/:id` only matches one segment, so a
 * missing entry makes refresh / pasted links fall through to "Cannot GET".
 *
 * v4 IA: "My Fueling Plans" (`/plans`) is the history list; an individual plan is
 * a detail view (`/plan/:id`), not a nav tab. Preferences is a modal (journey
 * §9) — not a route — opened from the sub-nav / contextual cards. A legacy
 * `?view=preferences` (or a `/preferences` deep-link) lands on `/dashboard`
 * with `?prefs=1`. `PreferencesDeepLink` (app gate) opens the modal on any
 * route carrying that param, then strips it.
 */
/** Shopify theme links use `?view=dashboard` etc.; map those to SPA paths once. */
const LEGACY_VIEW_ROUTES: Record<string, string> = {
  dashboard: "/dashboard",
  landing: "/landing",
  preferences: "/dashboard?prefs=1",
  plans: "/plans",
  // Merged into My Training — legacy theme links land on the new surface.
  activities: "/my-training",
  planner: "/my-training",
  "my-training": "/my-training",
  "my-fuel": "/my-fuel"
};

/**
 * Redirect a retired path to `/my-training`, preserving the query string
 * (`?week=`, `?import=1`, `?view=`) so shared planner links keep working.
 */
function RedirectToMyTraining() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/my-training", search: location.search }} replace />;
}

/**
 * Index (`/`) redirect. Normally sends the proxy root to `/landing`, but if the
 * URL carries a legacy `?view=` that maps to a real route, it forwards there
 * instead — preserving the other query params.
 *
 * This MUST honor `?view=` rather than unconditionally going to `/landing`:
 * the connector OAuth callback lands the browser on the proxy root with
 * `?view=dashboard&connected=<provider>` (`connectors/core/routes.ts`). The
 * bare `<Navigate to="/landing">` used to win the effect-order race against
 * `LegacyViewQueryRedirect` (and dropped the query), bouncing a just-connected
 * user back to Landing instead of the Dashboard. Resolving the same
 * destination here makes the two redirects agree, so the race is harmless.
 */
function IndexRedirect() {
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view");
  // OAuth callback params must never be dropped on the index → landing hop.
  // If `view` is missing/stripped by the theme but `connected` / `connect_error`
  // remain, still land on the dashboard so ConnectorReturnStatus can fire.
  const connectorReturn =
    searchParams.get("connected") || searchParams.get("connect_error")
      ? "/dashboard"
      : null;
  const target = (view ? LEGACY_VIEW_ROUTES[view] : null) ?? connectorReturn;
  if (target) {
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    const [targetPath, targetQuery] = target.split("?");
    if (targetQuery) {
      for (const [k, v] of new URLSearchParams(targetQuery)) next.set(k, v);
    }
    const qs = next.toString();
    return <Navigate to={`${targetPath}${qs ? `?${qs}` : ""}`} replace />;
  }
  return <Navigate to="/landing" replace />;
}

/**
 * Scroll the storefront page back to the top on SPA route changes. The app proxy
 * serves the SPA inside the Shopify theme layout (header/footer), so the scroll
 * container is the document — React Router doesn't reset it on client navigations.
 * Keyed on `pathname` only so hash anchors (`/my-fuel#box-0`) stay intact.
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // Some themes scroll `html`/`body` instead of `window`.
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
}

function LegacyViewQueryRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const view = searchParams.get("view");
    if (!view) return;
    const target = LEGACY_VIEW_ROUTES[view];
    if (!target) return;
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    // `target` may itself carry a query (e.g. preferences → /dashboard?prefs=1).
    const [targetPath, targetQuery] = target.split("?");
    if (targetQuery) {
      for (const [k, v] of new URLSearchParams(targetQuery)) next.set(k, v);
    }
    const qs = next.toString();
    navigate(`${targetPath}${qs ? `?${qs}` : ""}${location.hash}`, { replace: true });
  }, [searchParams, navigate, location.hash]);

  return null;
}

export function AppRoutes() {
  return (
    <>
      <PreferencesDeepLink />
      <ScrollToTop />
      <LegacyViewQueryRedirect />
      <Suspense fallback={<RouteShellFallback />}>
        <Routes>
          <Route path="/" element={<IndexRedirect />} />
          <Route path="/landing" element={<LandingScreen />} />
          {/* Public FAQ library — reached from the landing's "See more FAQs". */}
          <Route path="/faq" element={<FaqScreen />} />
          {/* Manual entry now lives in the empty planner + Add-workout flow (Phase 1.5);
              the standalone onboarding page is retired — redirect it. */}
          <Route path="/onboarding" element={<RedirectToMyTraining />} />
          {/* Retired Strava OAuth landing screen — connector connect progress
              now renders on the Dashboard (`?view=dashboard&connected=…`). */}
          <Route
            path="/connecting/:provider"
            element={<NavigatePreserveSearch to="/dashboard" />}
          />
          <Route path="/dashboard" element={<DashboardScreen />} />
          <Route path="/my-training" element={<MyTrainingScreen />} />
          <Route path="/my-fuel" element={<MyFuelScreen />} />
          {/* Retired surfaces — redirect to the merged My Training page. */}
          <Route path="/planner" element={<RedirectToMyTraining />} />
          <Route path="/plan" element={<FuelPlanScreen />} />
          <Route path="/plan/pending/:requestId" element={<PlanPendingScreen />} />
          <Route path="/plan/:id" element={<FuelPlanScreen />} />
          <Route path="/plans" element={<PlansScreen />} />
          {/* Retired race-day screen — bookmarks land on the My Fuel timeline. */}
          <Route path="/plans/event/:journeyId/race-day" element={<RedirectToMyFuelRaceDay />} />
          <Route path="/race-day" element={<RedirectToMyFuelRaceDay />} />
          <Route path="/activities" element={<RedirectToMyTraining />} />
          {/* Public read-only shared plan — the v4 re-port of plan-share.ejs.
              The signed token in the URL is the capability; no login needed. */}
          <Route path="/share/:token" element={<ShareScreen />} />
          {/* Preferences is a modal (journey §9). The legacy `/preferences`
              deep-link redirects to the Dashboard, which auto-opens the modal. */}
          <Route path="/preferences" element={<Navigate to="/dashboard?prefs=1" replace />} />
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
      </Suspense>
    </>
  );
}
